// HPS File Validator Types

export interface FieldSpec {
  name: string;
  // For fixed-width specs
  start?: number;
  end?: number;
  length?: number;
  // For delimited specs
  num?: number;
  min?: number;
  max?: number;
  format?: string;
  dataType: 'AN' | 'N' | 'DT' | 'TM' | 'D8' | 'T6';
  required: boolean;
  usage: 'Required' | 'Conditional' | 'Optional';
  values?: string[];
}

export interface RecordSpec {
  name: string;
  code: string;
  required: boolean;
  maxOccurrences: number;
  fields: FieldSpec[];
}

export interface FileSpec {
  name: string;
  version: string;
  description: string;
  // For fixed-width files this is the expected record length. For delimited files it may be 0.
  recordLength?: number;
  delimiter?: string;
  newline?: string;
  fileNaming?: string;
  specPdfUrl?: string;
  records: Record<string, RecordSpec>;
}

export interface HpsSpecs {
  claimstatus30: FileSpec;
  eligibility31: FileSpec;
  eligibility30: FileSpec;
  individualusagebenefit30: FileSpec;
  planbenefit30: FileSpec;
  standardbenefitentity30: FileSpec;
}

export interface ValidationError {
  line: number;
  field: string;
  message: string;
  expected?: string;
  actual?: string;
  severity: 'error' | 'warning';
  fixSuggestion?: string;
}

export interface ParsedRecord {
  line: number;
  recordType: string;
  rawData: string;
  fields: Record<string, string>;
}

export interface ValidationResults {
  isValid: boolean;
  totalRecords: number;
  validRecords: number;
  errors: ValidationError[];
  warnings: ValidationError[];
  records: ParsedRecord[];
  recordCounts: Record<string, number>;
}

export interface AIInsight {
  type: 'success' | 'warning' | 'error' | 'info';
  title: string;
  description: string;
  action: string;
}

export interface CommonIssue {
  issue: string;
  count: number;
  affectedLines: number[];
  suggestion: string;
}

export type SpecType = 'claimstatus30' | 'eligibility31' | 'eligibility30' | 'individualusagebenefit30' | 'planbenefit30' | 'standardbenefitentity30';

// ─── Mapping Types ───────────────────────────────────────────────────

export type TargetSpecType = 'claimstatus30' | 'eligibility31' | 'eligibility30' | 'individualusagebenefit30' | 'planbenefit30' | 'standardbenefitentity30';

export type MappingConfidence = 'high' | 'medium' | 'low' | 'unmapped';

export interface FieldMapping {
  sourceField: string;
  sourceExample: string;
  targetRecord: string;
  targetField: string;
  targetFieldNum: number;
  confidence: MappingConfidence;
  transformationNote: string;
  dataType: string;
  required: boolean;
  usage: 'Required' | 'Conditional' | 'Optional';
}

export interface MappingResultSummary {
  totalSourceFields: number;
  mappedFields: number;
  unmappedFields: number;
}

export interface UnmappedSourceField {
  field: string;
  example?: string;
  examples?: string[];
  dataType?: string;
}

export type UnmappedSourceFieldEntry = string | UnmappedSourceField;

export interface MappingResults {
  sourceFormat: string;
  targetSpec: string;
  targetVersion: string;
  summary: MappingResultSummary;
  mappings: FieldMapping[];
  unmappedSourceFields: UnmappedSourceFieldEntry[];
  unmappedTargetFields: {
    record: string;
    field: string;
    required: boolean;
    usage?: 'Required' | 'Conditional' | 'Optional';
    fieldNum?: number;
    expectedValue?: string;
  }[];
  recommendations: string[];
  warnings?: string[];
  mappingSource?: 'server-llm' | 'local-fallback';
  fallbackReason?: string;
  telemetry?: {
    batchCount?: number;
    fulfilledBatchCount?: number;
    failedBatchCount?: number;
    sourceFieldCount?: number;
    targetFieldCount?: number;
    targetCandidateCount?: number;
    llmCallCount?: number;
    responseRetryCount?: number;
    latencyMs?: number;
    conflictsResolved?: number;
    mappedFieldCount?: number;
    unmappedSourceFieldCount?: number;
    unmappedTargetFieldCount?: number;
    warningCount?: number;
    usage?: {
      promptTokens?: number;
      completionTokens?: number;
      totalTokens?: number;
    };
    finishReasons?: Record<string, number>;
  };
}

// ─── Guardrails & LLM Settings ───────────────────────────────────────────────

export interface GuardrailViolation {
  passed: false;
  category: string;
  message: string;
}

export interface LLMSettings {
  reasoning_effort: 'low' | 'medium' | 'high';
  max_tokens: number;        // UI label; sent to the service as max_completion_tokens
  max_lines: number;         // 1-100, max lines to read from uploaded file
}

export const DEFAULT_LLM_SETTINGS: LLMSettings = {
  reasoning_effort: 'medium',
  max_tokens: 4096,
  max_lines: 30,
};

// ─── LLM-as-a-Judge Metrics ──────────────────────────────────────────────────

export interface LLMJudgeMetrics {
  accuracy: number;      // 0-100
  completeness: number;  // 0-100
  consistency: number;   // 0-100
  overall: number;       // 0-100
  summary: string;
  concerns: string[];
  reasoning: string;
  latencyMs: number;
  source: 'llm-judge' | 'llm-judge-consensus' | 'heuristic' | 'validation-metrics';
  judgeRuns?: number;
}
